import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ORIENTATIONS } from '@seencast/shared';
import { Drawer } from '../../components/Drawer.tsx';
import { ConfirmDialog } from '../../components/Modal.tsx';
import { Button, Chip, StatusDot, SelectField, TextField, Loading } from '../../components/ui.tsx';
import {
  useScreens,
  useUpdateScreen,
  useUnpairScreen,
  useDeleteScreen,
  useSetScreenOverlay,
  useScreenCommand,
  useSetScreenLayout,
  screenOverlayId,
  screenLayoutId,
} from '../../api/screens.ts';
import { useLayouts } from '../../api/layouts.ts';
import { useMediaNameResolver } from '../../api/media.ts';
import { useSchedulePreview } from '../../api/schedules.ts';
import { useOverlays } from '../../api/overlays.ts';
import { useOrg, orgDefaultOverlayId } from '../../api/org.ts';
import { ApiError } from '../../api/client.ts';
import { usePermissions } from '../../auth/session.tsx';
import { useLivePresence } from '../../live/LivePresenceContext.tsx';
import { liveStatusForScreen, nowPlayingLabel } from '../../live/presence.ts';
import { timezoneOptions } from '../../lib/tz.ts';
import { formatRelative, formatDuration, titleCase } from '../../lib/format.ts';
import { ScreenPreviewModal } from './ScreenPreviewModal.tsx';
import { ScreenSnapshots } from './ScreenSnapshots.tsx';

export function ScreenDetailDrawer({ screenId, onClose }: { screenId: string; onClose: () => void }) {
  const screens = useScreens();
  const perms = usePermissions();
  const update = useUpdateScreen();
  const unpair = useUnpairScreen();
  const del = useDeleteScreen();
  const preview = useSchedulePreview(screenId);
  const overlays = useOverlays();
  const org = useOrg();
  const setScreenOverlay = useSetScreenOverlay();
  const setScreenLayout = useSetScreenLayout();
  const layouts = useLayouts();
  const command = useScreenCommand();
  const { presence } = useLivePresence();
  const resolveMediaName = useMediaNameResolver();
  const tzOptions = useMemo(() => timezoneOptions(), []);

  const screen = screens.data?.find((s) => s.id === screenId);
  const [confirm, setConfirm] = useState<null | 'unpair' | 'delete' | 'reload'>(null);
  const [previewing, setPreviewing] = useState(false);
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [orientation, setOrientation] = useState<'landscape' | 'portrait'>('landscape');
  const [timezone, setTimezone] = useState('America/Los_Angeles');

  function startEdit() {
    if (!screen) return;
    setName(screen.name);
    setLocation(screen.location ?? '');
    setOrientation(screen.orientation);
    setTimezone(screen.timezone);
    setEdit(true);
  }

  function save() {
    if (!screen) return;
    update.mutate(
      { id: screen.id, body: { name, location, orientation, timezone } },
      {
        onSuccess: () => {
          toast.success('Screen updated');
          setEdit(false);
        },
        onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Update failed'),
      },
    );
  }

  // Not-live-connected copy is fixed (docs/06 §Live controls): the command was
  // accepted but no player is connected right now, so it applies on the next poll.
  const NOT_CONNECTED = 'Screen is not live-connected; it will pick changes up on its next poll';

  function runCommand(cmd: 'reload' | 'refresh_manifest', deliveredMsg: string) {
    if (!screen) return;
    command.mutate(
      { id: screen.id, command: cmd },
      {
        onSuccess: (res) => (res.delivered ? toast.success(deliveredMsg) : toast(NOT_CONNECTED)),
        onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Command failed'),
      },
    );
  }

  if (screens.isLoading) {
    return (
      <Drawer title="Screen" onClose={onClose}>
        <Loading />
      </Drawer>
    );
  }
  if (!screen) {
    return (
      <Drawer title="Screen" onClose={onClose}>
        <p className="text-dim">This screen no longer exists.</p>
      </Drawer>
    );
  }

  return (
    <Drawer
      title={screen.name}
      onClose={onClose}
      headerActions={
        !edit ? (
          <>
            <Button size="sm" onClick={() => setPreviewing(true)}>
              Preview now
            </Button>
            {perms.canManageContent && (
              <>
                <Button
                  size="sm"
                  onClick={() => runCommand('refresh_manifest', 'Asked the screen to refresh its content')}
                  disabled={command.isPending}
                >
                  Refresh content
                </Button>
                <Button size="sm" onClick={() => setConfirm('reload')} disabled={command.isPending}>
                  Reload player
                </Button>
                <Button size="sm" onClick={startEdit}>
                  Edit
                </Button>
              </>
            )}
          </>
        ) : undefined
      }
    >
      <div className="row mb-4">
        <StatusDot status={liveStatusForScreen(screen, presence)} />
        <span className="text-dim">· last seen {formatRelative(screen.last_seen_at)}</span>
      </div>
      {(() => {
        const playing = nowPlayingLabel(screen, presence, resolveMediaName);
        return playing ? (
          <p className="text-dim mb-4" style={{ marginTop: 'calc(-1 * var(--sp-2))' }}>
            Now playing: <span style={{ color: 'var(--text)' }}>{playing}</span>
          </p>
        ) : null;
      })()}

      {edit ? (
        <>
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <TextField label="Location" value={location} onChange={(e) => setLocation(e.target.value)} />
          <SelectField
            label="Orientation"
            value={orientation}
            onChange={(e) => setOrientation(e.target.value as 'landscape' | 'portrait')}
          >
            {ORIENTATIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </SelectField>
          <SelectField label="Timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
            {tzOptions.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </SelectField>
          <div className="row">
            <Button variant="primary" onClick={save} disabled={update.isPending}>
              Save
            </Button>
            <Button onClick={() => setEdit(false)}>Cancel</Button>
          </div>
        </>
      ) : (
        <dl className="dl">
          <dt>Location</dt>
          <dd>{screen.location ?? '—'}</dd>
          <dt>Orientation</dt>
          <dd>{screen.orientation}</dd>
          <dt>Timezone</dt>
          <dd>{screen.timezone}</dd>
          <dt>Player</dt>
          <dd>{screen.player_version ?? '—'}</dd>
          <dt>Groups</dt>
          <dd>
            {screen.groups.length ? (
              <div className="chip-row">
                {screen.groups.map((g) => (
                  <Chip key={g.id}>{g.name}</Chip>
                ))}
              </div>
            ) : (
              '—'
            )}
          </dd>
        </dl>
      )}

      {perms.canManageContent && (
        <>
          <div className="divider" />
          <div className="card-title">Overlay</div>
          {/* Two-state: inherit the org default or a specific overlay. Explicit
              opt-out deferred — needs a dedicated column, not a sentinel. */}
          {(() => {
            const current = screenOverlayId(screen);
            const selectValue = current ?? '';
            const defaultId = orgDefaultOverlayId(org.data);
            const defaultName = overlays.data?.find((o) => o.id === defaultId)?.name;
            const inheritLabel = defaultName
              ? `Inherit org default (${defaultName})`
              : 'Inherit org default (none set)';
            return (
              <SelectField
                label="Shown above content"
                value={selectValue}
                onChange={(e) => {
                  const v = e.target.value;
                  setScreenOverlay.mutate(
                    { id: screen.id, overlay_id: v === '' ? null : v },
                    {
                      onSuccess: () => toast.success('Overlay updated'),
                      onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Failed'),
                    },
                  );
                }}
                disabled={setScreenOverlay.isPending}
              >
                <option value="">{inheritLabel}</option>
                {(overlays.data ?? []).map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </SelectField>
            );
          })()}

          <div className="divider" />
          <div className="card-title">Layout</div>
          {/* None = fullscreen (a single schedule-resolved zone). An org layout
              splits the screen into zones (docs/09 §C1). */}
          <SelectField
            label="Screen layout"
            value={screenLayoutId(screen) ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setScreenLayout.mutate(
                { id: screen.id, layout_id: v === '' ? null : v },
                {
                  onSuccess: () => toast.success('Layout updated'),
                  onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Failed'),
                },
              );
            }}
            disabled={setScreenLayout.isPending}
          >
            <option value="">None (fullscreen)</option>
            {(layouts.data ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </SelectField>
        </>
      )}

      <div className="divider" />
      <div className="card-title">Today&apos;s schedule</div>
      {preview.isLoading ? (
        <Loading />
      ) : preview.data && preview.data.source !== 'none' ? (
        <>
          <p>
            Now resolving to <strong>{preview.data.playlist?.name}</strong> ({preview.data.items.length} item
            {preview.data.items.length === 1 ? '' : 's'}).
          </p>
          <ul className="text-dim" style={{ paddingLeft: 18, fontSize: 'var(--fs-sm)' }}>
            {preview.data.items.map((it) => (
              <li key={it.id}>
                {/* Operators read slide names, not engine jargon (UX audit P1-5) —
                    the media's human name leads; kind/fit stay as dim metadata. */}
                <span style={{ color: 'var(--text)' }}>{it.name ?? titleCase(it.type)}</span>
                <span> · {formatDuration(it.duration_ms)} · {it.fit}</span>
              </li>
            ))}
          </ul>
          {preview.data.candidates.length > 1 && (
            <p className="hint">
              {preview.data.candidates.length} schedules overlap right now; highest priority wins.
            </p>
          )}
        </>
      ) : (
        <p className="text-dim">Nothing scheduled right now — the screen shows the fallback.</p>
      )}

      {/* Live snapshot — a content-management action (editors+), gated exactly
          like the screen's other mutations. Per-user scope isn't exposed to the
          client (Me carries only role, not the caller's own scope set), so we use
          the same canManageContent gate every other write here uses. */}
      {perms.canManageContent && <ScreenSnapshots screen={screen} />}

      {perms.canManageContent && (
        <>
          <div className="divider" />
          <div className="row">
            {screen.status !== 'never_paired' && (
              <Button variant="danger" onClick={() => setConfirm('unpair')}>
                Unpair
              </Button>
            )}
            <Button variant="danger" onClick={() => setConfirm('delete')}>
              Delete screen
            </Button>
          </div>
        </>
      )}

      {previewing && (
        <ScreenPreviewModal
          screenId={screen.id}
          screenName={screen.name}
          onClose={() => setPreviewing(false)}
        />
      )}

      {confirm === 'reload' && (
        <ConfirmDialog
          title="Reload the player?"
          message="The screen will briefly go blank while the player restarts and reloads its content. Use this if a screen looks stuck."
          confirmLabel="Reload player"
          busy={command.isPending}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            runCommand('reload', 'Asked the screen to reload its player');
            setConfirm(null);
          }}
        />
      )}
      {confirm === 'unpair' && (
        <ConfirmDialog
          title="Unpair screen?"
          message="The device will lose its token and stop playing until you pair it again. The screen record and its schedules stay."
          confirmLabel="Unpair"
          danger
          busy={unpair.isPending}
          onCancel={() => setConfirm(null)}
          onConfirm={() =>
            unpair.mutate(screen.id, {
              onSuccess: () => {
                toast.success('Screen unpaired');
                setConfirm(null);
              },
              onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
            })
          }
        />
      )}
      {confirm === 'delete' && (
        <ConfirmDialog
          title="Delete screen?"
          message={`“${screen.name}” will be removed permanently, along with its group memberships.`}
          confirmLabel="Delete"
          danger
          busy={del.isPending}
          onCancel={() => setConfirm(null)}
          onConfirm={() =>
            del.mutate(screen.id, {
              onSuccess: () => {
                toast.success('Screen deleted');
                onClose();
              },
              onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
            })
          }
        />
      )}
    </Drawer>
  );
}
